/* eslint-disable import/no-unresolved,react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation, useQuery } from 'react-query';
import { ResetTFAComponent } from 'assets/components';
import Countdown from '../../../utilities/counter';
import { ACTIONS_AFTER_RESET_SECRET } from '../constants';
import { RECAPTCHA_EVENTS } from '../../Onboarding/constants';
import {
  USER_LOGIN,
  EMAIL_TOTP_CHECK,
  PHONE_VERIFICATION_CHECK,
  PHONE_VERIFICATION_SEND,
} from '../../Onboarding/mutations';
import { showAlert } from '../../../utilities/notifications';
import { FLOWS, aliasUser, identifyUser, sendStats } from '../../../services/mixpanel';
import { MXP_SIGN_IN_SUCCESS, MXP_SIGN_IN_TFA_REQUEST } from '../../Onboarding/mixpanel-events';
import {
  PHONE_VERIFICATION_SEND_AUTHY,
  RESET_OTP,
  VERIFY_RESET_OTP,
} from '../../../utilities/sharedMutations';
import { PASSWORD_PUBLIC_KEY } from '../../../utilities/sharedQueries';
import { useSetting } from '../../../utilities/sharedHooks/settings';
import { encryptWithPublicKey } from '../../../utilities/helpers';
import RecaptchaModal from '../../shared/RecaptchaModal';
import { cacheRecaptchaV3failure, clearRecaptchaV3failure } from '../../../utilities/caching';

const phoneCountdown = new Countdown();

const SecurityTOTPResetSecretTFA = () => {
  const { t: translate } = useTranslation();

  const { state } = useLocation();
  const history = useHistory();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);
  const [recV2Token, setRecV2Token] = useState('');

  const [isTOTPEnabled, setIsTOTPEnabled] = useState(false);

  const [isLoginRequest, setIsLoginRequest] = useState(false);
  const [isResetPasswordRequest, setIsResetPasswordRequest] = useState(false);

  const [checkByVeriryResetOtp, setCheckByVerifyResetOtp] = useState(false);
  const [timer, setTimer] = useState();
  const [form, setForm] = useState({
    confirmed_phone_number: `${state?.country_code}${state?.phone_number}`,
    code: '',
  });

  const [enablePasswordKMSEncryption] = useSetting(
    'app_features',
    'feature_toggle_enable_password_kms_encryption',
    false,
    false
  );

  const { data: publicKeyData } = useQuery(PASSWORD_PUBLIC_KEY, {
    enabled: !!enablePasswordKMSEncryption,
  });

  const { mutate: login, isLoading } = useMutation(USER_LOGIN, {
    onSettled: (data, error) => {
      if (error) {
        if (error?.response?.status === 307) {
          setIsOpenRecV2(true);
          cacheRecaptchaV3failure();
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          showAlert({ message: error.message, type: 'danger' });
        }
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        sendStats(
          MXP_SIGN_IN_SUCCESS,
          { tfaRequired: data?.['2fa_required'], success: !error },
          FLOWS.SIGN_IN
        );
        aliasUser(data);
        identifyUser(data);
        history.push('/onboarding/totp-secret', {
          resetSecretKey: true,
          action: ACTIONS_AFTER_RESET_SECRET.LOGIN,
          email: state?.email || state?.username,
        });
      }
    },
  });

  const { mutate: resetOTP, isLoading: isLoadingResetOtp } = useMutation(RESET_OTP, {
    onSettled: (_, error) => {
      sendStats(MXP_SIGN_IN_TFA_REQUEST, {}, FLOWS.SIGN_IN);
      if (error) {
        if (error?.response?.status === 307) {
          setIsOpenRecV2(true);
          cacheRecaptchaV3failure();
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          showAlert({ message: error.message, type: 'danger' });
        }
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        setCheckByVerifyResetOtp(true);
        phoneCountdown.start();
      }
    },
  });

  const {
    mutate: checkPhoneNumberVerificationResetOtp,
    isLoading: checkingPhoneNumberVerificationResetOtp,
  } = useMutation(VERIFY_RESET_OTP, {
    onSettled: (data, error) => {
      if (error) {
        if (error?.response?.status === 307) {
          setIsOpenRecV2(true);
          cacheRecaptchaV3failure();
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          showAlert({ message: error.message, type: 'danger' });
        }
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        history.push('/onboarding/totp-secret', {
          resetSecretKey: true,
          email: state?.email || state?.username,
          action: ACTIONS_AFTER_RESET_SECRET.RESET_PASSWORD,
          secretBase32: data?.secret?.base32,
          secretQeCodeUrl: data?.secret?.qr_code_url,
          ...state,
        });
      }
      setCheckByVerifyResetOtp(false);
    },
  });

  const { mutate: checkPhoneNumberVerification, isLoading: checkingPhoneNumberVerification } =
    useMutation(PHONE_VERIFICATION_CHECK, {
      onSettled: (data, error) => {
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          history.push('/onboarding/totp-secret', {
            resetSecretKey: true,
            resetKey: state?.resetKey,
            email: state?.email || state?.username,
            action: ACTIONS_AFTER_RESET_SECRET.RESET_PASSWORD,
            secretBase32: data?.secret?.base32,
            secretQeCodeUrl: data?.secret?.qr_code_url,
            ...state,
          });
        }
      },
    });

  const { mutate: sendPhoneVerificationCode, isLoading: isSendingVerificationCode } = useMutation(
    PHONE_VERIFICATION_SEND,
    {
      onSettled: (data, error) => {
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          phoneCountdown.start();
        }
      },
    }
  );

  const { mutate: requestPhoneVerification, isLoading: isLoadingRequestPhoneVerification } =
    useMutation(PHONE_VERIFICATION_SEND_AUTHY, {
      onSettled: (_, error) => {
        sendStats(MXP_SIGN_IN_TFA_REQUEST, {}, FLOWS.SIGN_IN);
        if (error) {
          if (error?.response?.status === 307) {
            setIsOpenRecV2(true);
            cacheRecaptchaV3failure();
          } else {
            setIsOpenRecV2(false);
            clearRecaptchaV3failure();
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          phoneCountdown.start();
        }
      },
    });

  const handleRequestSMSCodeForResetPassword = async (totpEnabled) => {
    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.VERIFY_PHONE_NUMBER);
      if (!token) {
        return false;
      }

      if (state?.action === ACTIONS_AFTER_RESET_SECRET.LOGIN) {
        if (totpEnabled) {
          resetOTP({ ...state, token });
        } else {
          requestPhoneVerification({ ...state, token });
        }
      } else if (state?.action === ACTIONS_AFTER_RESET_SECRET.RESET_PASSWORD) {
        if (totpEnabled) {
          resetOTP({ email: state?.email, token });
        } else {
          sendPhoneVerificationCode({
            via: 'sms',
            country_code: state.country_code,
            phone_number: state.phone_number,
            verificationId: state?.verification_id,
            token,
          });
        }
      }
    } catch (err) {
      return false;
    }
    return true;
  };

  const { mutate: checkTOTP, isLoading: isCheckingTOTPLoading } = useMutation(EMAIL_TOTP_CHECK, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        setIsTOTPEnabled(data?.totp_enabled);
        handleRequestSMSCodeForResetPassword(data?.totp_enabled);
      }
    },
  });

  useEffect(() => {
    if (!state?.action) {
      history.push('/onboarding/login');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    switch (state?.action) {
      case ACTIONS_AFTER_RESET_SECRET.LOGIN:
        try {
          setIsLoginRequest(true);

          const sendData = {
            username: state.username,
            password: state.password,
            otpauth: form.code,
          };

          const token = await executeRecaptcha(RECAPTCHA_EVENTS.LOGIN);
          if (!token) {
            return false;
          }
          if (enablePasswordKMSEncryption) {
            const encryptedPassword = await encryptWithPublicKey(
              publicKeyData.publicKey,
              state.password
            );
            login({ ...sendData, password: encryptedPassword, token });
          } else {
            login({ ...sendData, token });
          }
        } catch (err) {
          return false;
        }
        break;

      case ACTIONS_AFTER_RESET_SECRET.RESET_PASSWORD: {
        setIsResetPasswordRequest(true);
        try {
          const token = await executeRecaptcha(RECAPTCHA_EVENTS.VERIFY_PHONE_NUMBER);
          if (!token) {
            return false;
          }

          if (checkByVeriryResetOtp) {
            checkPhoneNumberVerificationResetOtp({
              email: state?.email,
              otpauth: form.code,
              token,
            });
          } else {
            checkPhoneNumberVerification({
              ...state,
              verification_code: form.code,
              verification_id: state?.verification_id,
              token,
              attachSecret: true,
              email: state?.email,
            });
          }
        } catch (err) {
          return false;
        }
        break;
      }

      default:
        return true;
    }

    return true;
  };

  const handleRequestPhoneNumberVerification = () => {
    switch (state?.action) {
      case ACTIONS_AFTER_RESET_SECRET.LOGIN: {
        checkTOTP({ email: state?.username?.toLowerCase() });
        break;
      }
      case ACTIONS_AFTER_RESET_SECRET.RESET_PASSWORD: {
        checkTOTP({ email: state?.email?.toLowerCase() });
        break;
      }
      default:
        break;
    }
    return true;
  };

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  useEffect(() => {
    phoneCountdown.bind(setTimer);
    handleRequestPhoneNumberVerification();
    return () => {
      phoneCountdown.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderButtons = useCallback(
    () => (
      <div className="totp-button-container">
        <button
          className="btn btn-primary w-100 py-3 text-bold"
          type="submit"
          disabled={
            form.code === '' ||
            isLoading ||
            checkingPhoneNumberVerification ||
            checkingPhoneNumberVerificationResetOtp ||
            isCheckingTOTPLoading ||
            isSendingVerificationCode
          }
          loading={
            isLoading ||
            checkingPhoneNumberVerification ||
            checkingPhoneNumberVerificationResetOtp ||
            isCheckingTOTPLoading ||
            isSendingVerificationCode
              ? 'true'
              : 'false'
          }
          form="confirm-phone-form">
          {translate('confirm_button_label')}
        </button>
      </div>
    ),
    [
      form?.code,
      isLoading,
      checkingPhoneNumberVerification,
      checkingPhoneNumberVerification,
      isCheckingTOTPLoading,
      isSendingVerificationCode,
    ]
  );

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    const sendData = {
      username: state.username,
      password: state.password,
      otpauth: form.code,
    };

    if (isLoginRequest) {
      setIsLoginRequest(false);
      if (enablePasswordKMSEncryption) {
        const encryptedPassword = await encryptWithPublicKey(
          publicKeyData.publicKey,
          state.password
        );
        login({ ...sendData, password: encryptedPassword, token: tokenV2 });
      } else {
        login({ ...sendData, token: tokenV2 });
      }
    } else if (isResetPasswordRequest) {
      setIsResetPasswordRequest(false);
      if (checkByVeriryResetOtp) {
        checkPhoneNumberVerificationResetOtp({
          email: state?.email,
          otpauth: form.code,
          token: tokenV2,
        });
      } else {
        checkPhoneNumberVerification({
          ...state,
          verification_code: form.code,
          verification_id: state?.verification_id,
          token: tokenV2,
          attachSecret: true,
          email: state?.email,
        });
      }
    } else if (state?.action === ACTIONS_AFTER_RESET_SECRET.LOGIN) {
      if (isTOTPEnabled) {
        resetOTP({ ...state, token: tokenV2 });
      } else {
        requestPhoneVerification({ ...state, token: tokenV2 });
      }
    } else if (state?.action === ACTIONS_AFTER_RESET_SECRET.RESET_PASSWORD) {
      if (isTOTPEnabled) {
        resetOTP({ email: state?.email, token: tokenV2 });
      } else {
        sendPhoneVerificationCode({
          via: 'sms',
          country_code: state.country_code,
          phone_number: state.phone_number,
          verificationId: state?.verification_id,
          token: tokenV2,
        });
      }
    }
  };

  return (
    <>
      <ResetTFAComponent
        state={state}
        form={form}
        timer={timer}
        isLoadingResetOtp={isLoadingResetOtp}
        isLoadingRequestPhoneVerification={isLoadingRequestPhoneVerification}
        setCheckByVerifyResetOtp={setCheckByVerifyResetOtp}
        isCheckingTOTPLoading={isCheckingTOTPLoading}
        handleSubmit={handleSubmit}
        handleRequestPhoneNumberVerification={handleRequestPhoneNumberVerification}
        handleChange={handleChange}
        renderButtons={renderButtons}
      />
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={
          isLoading ||
          isLoadingResetOtp ||
          isLoadingRequestPhoneVerification ||
          isSendingVerificationCode
        }
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default SecurityTOTPResetSecretTFA;
